import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../api/client';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './FormStyles.css';

const EditProduct = () => {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Basic fields
  const [name, setName] = useState('');
  const [aliasName, setAliasName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [price, setPrice] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [productType, setProductType] = useState('');
  const [status, setStatus] = useState('active');
  
  // Images - store objects with url and isNew flag
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  
  // Meta data
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  
  // Advanced
  const [specs, setSpecs] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  
  // Analytics
  const [searchCount, setSearchCount] = useState(0);
  const [enquiryCount, setEnquiryCount] = useState(0);
  const [quoteCount, setQuoteCount] = useState(0);
  
  // State
  const [originalProduct, setOriginalProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [productRes, categoryRes, typesRes] = await Promise.all([
        api.get(`/products/${id}`, { timeout: 10000 }),
        api.get(`/categories`, { timeout: 10000 }),
        api.get(`/products/types`, { timeout: 10000 })
      ]);

      console.log('Product response:', productRes.data);

      // Handle new response format: { success: true, data: {...} }
      let product;
      if (productRes.data.success) {
        product = productRes.data.data;
      } else {
        product = productRes.data;
      }

      setOriginalProduct(product);
      
      // Basic fields
      setName(product.name || '');
      setAliasName(product.alias_name || '');
      setProductCode(product.product_code || '');
      setProductType(product.product_type || '');
      setPrice(String(product.price || ''));
      setCostPrice(String(product.cost_price || ''));
      setMrp(String(product.mrp || ''));
      setDescription(product.description || '');
      setStatus(product.status || 'active');

      // Analytics
      setSearchCount(product.search_count || 0);
      setEnquiryCount(product.enquiry_count || 0);
      setQuoteCount(product.quote_count || 0);
   
      // Images - existing images from server
      setImages(
        (product.images || []).map(img => ({
          url: /^https?:\/\//i.test(img.image) ? img.image : `${API_BASE}/uploads/products/${img.image}`,
          filename: img.image,
          isNew: false
        }))
      );
      
      // Relationships
      setCategoryId(String(product.category_id || ''));

      // Handle categories response
      const categoriesData = categoryRes.data.data || categoryRes.data || [];
      setCategories(categoriesData);

      // Handle product types response
      const typesData = typesRes.data.data || typesRes.data || [];
      setProductTypes(typesData);

      // Advanced
      setSpecs(product.specs || []);
      setKeywords(product.keywords?.map(k => k.keyword) || []);

    } catch (err) {
      console.error('Fetch failed:', err);
      
      let errorMessage = 'Unable to fetch product';
      if (err.response?.status === 404) {
        errorMessage = 'Product not found';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      alert(errorMessage);
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleNewImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setNewImagePreviews(prev => [...prev, ...newPreviews]);
    setNewImages(prev => [...prev, ...files]);
  };

  const removeExistingImage = (index) => {
    if (window.confirm('Are you sure you want to remove this image?')) {
      setImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => {
      const newPreviews = prev.filter((_, i) => i !== index);
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });
  };

  const updateSpec = (index, field, value) => {
    const newSpecs = [...specs];
    newSpecs[index][field] = value;
    setSpecs(newSpecs);
  };

  const addSpec = () => {
    setSpecs([...specs, { key: '', value: '', unit: '' }]);
  };

  const removeSpec = (index) => {
    setSpecs(specs.filter((_, i) => i !== index));
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!originalProduct) return;

    // Validation
    if (!name.trim()) {
      alert('Product name is required');
      return;
    }
    if (!price || isNaN(parseFloat(price))) {
      alert('Valid price is required');
      return;
    }
    if (!categoryId) {
      alert('Category is required');
      return;
    }
    if (!productType) {
      alert('Product type is required');
      return;
    }
    if (!productCode.trim()) {
      alert('Product code is required');
      return;
    }

    try {
      setSaving(true);

      // Check if images changed
      const newImageFiles = newImages.filter(img => img.isNew !== false);
      const hasNewImages = newImageFiles.length > 0;
      const imageCountChanged = images.length !== (originalProduct.images?.length || 0);

      // Prepare update data
      const updatedData = {};

      if (name !== originalProduct.name) updatedData.name = name;
      if (aliasName !== (originalProduct.alias_name || '')) updatedData.alias_name = aliasName;
      if (productCode !== (originalProduct.product_code || '')) updatedData.product_code = productCode;
      if (productType !== (originalProduct.product_type || '')) updatedData.product_type = productType;
      if (description !== (originalProduct.description || '')) updatedData.description = description;
      if (status !== originalProduct.status) updatedData.status = status;

      if (price !== String(originalProduct.price)) {
        updatedData.price = parseFloat(price);
      }
      if (costPrice !== String(originalProduct.cost_price || '')) {
        updatedData.cost_price = costPrice ? parseFloat(costPrice) : null;
      }
      if (mrp !== String(originalProduct.mrp || '')) {
        updatedData.mrp = mrp ? parseFloat(mrp) : null;
      }

      if (categoryId !== String(originalProduct.category_id)) {
        updatedData.category_id = categoryId;
      }

      const originalSpecs = originalProduct.specs || [];
      const specsChanged = JSON.stringify(specs.filter(s => s.key && s.value)) !==
                          JSON.stringify(originalSpecs);
      if (specsChanged) {
        updatedData.specs = JSON.stringify(specs.filter(s => s.key && s.value));
      }

      const originalKeywords = originalProduct.keywords?.map(k => k.keyword) || [];
      const keywordsChanged = JSON.stringify(keywords) !== JSON.stringify(originalKeywords);
      if (keywordsChanged) {
        updatedData.keywords = JSON.stringify(keywords);
      }

      // If we need to upload new images, use FormData
      if (hasNewImages || imageCountChanged) {
        const formData = new FormData();

        // Add new images only
        newImages.forEach(image => {
          formData.append('images', image);
        });

        // Add other updated fields
        Object.keys(updatedData).forEach(key => {
          if (key === 'specs' || key === 'keywords') {
            formData.append(key, updatedData[key]);
          } else {
            formData.append(key, String(updatedData[key]));
          }
        });

        console.log('Updating with FormData (has new images)');

        const response = await api.patch(
          `/products/${id}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          }
        );

        console.log('Update response:', response.data);

      } else if (Object.keys(updatedData).length > 0) {
        // No new images, just JSON update
        console.log('Updating with JSON (no new images)');

        const response = await api.patch(
          `/products/${id}`,
          updatedData,
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000
          }
        );

        console.log('Update response:', response.data);
      } else {
        alert('No changes detected');
        setSaving(false);
        return;
      }

      alert('Product updated successfully');
      navigate('/products');
    } catch (err) {
      console.error('Update product error:', err);

      let errorMessage = 'Failed to update product';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="dashboard-main">
          <div className="loading-container">
            <div className="spinner large"></div>
            <p>Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="form-container">
          <div className="form-header">
            <h1>Edit Product</h1>
          </div>

          {/* Analytics */}
          <div className="analytics-section">
            <div className="analytics-card">
              <div className="analytics-label">Searches</div>
              <div className="analytics-value">{searchCount}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-label">Enquiries</div>
              <div className="analytics-value">{enquiryCount}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-label">Quotes</div>
              <div className="analytics-value">{quoteCount}</div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="modern-form">
            {/* Basic Information */}
            <div className="form-section">
              <h2 className="section-title">Basic Information</h2>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Product Name</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={saving}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Alias Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={aliasName}
                    onChange={(e) => setAliasName(e.target.value)}
                    disabled={saving}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Product Code</label>
                  <input
                    type="text"
                    className="form-input"
                    value={productCode}
                    onChange={(e) => setProductCode(e.target.value)}
                    disabled={saving}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label required">Category</label>
                  <select
                    className="form-select"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    required
                    disabled={saving}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="4"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="form-section">
              <h2 className="section-title">Pricing</h2>
              
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label className="form-label required">Selling Price</label>
                  <input
                    type="number"
                    required
                    className="form-input"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    step="0.01"
                    min="0"
                    disabled={saving}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Cost Price</label>
                  <input
                    type="number"
                    className="form-input"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    step="0.01"
                    min="0"
                    disabled={saving}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">MRP</label>
                  <input
                    type="number"
                    className="form-input"
                    value={mrp}
                    onChange={(e) => setMrp(e.target.value)}
                    step="0.01"
                    min="0"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>

            {/* Classification */}
            <div className="form-section">
              <h2 className="section-title">Classification</h2>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Product Type</label>
                  <select
                    className="form-select"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    required
                    disabled={saving}
                  >
                    <option value="">Select type</option>
                    <option value="camera">Camera</option>
                    <option value="recorder">Recorder</option>
                    <option value="storage">Storage</option>
                    <option value="networking">Networking</option>
                    <option value="power">Power</option>
                    <option value="cabling">Cabling</option>
                    <option value="accessory">Accessory</option>
                    <option value="display">Display</option>
                    <option value="access_control">Access Control</option>
                    <option value="alarm_security">Alarm & Security</option>
                    <option value="tool">Tool</option>
                    <option value="software">Software</option>
                    <option value="service">Service</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={saving}
                  >
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Existing Images */}
            {images.length > 0 && (
              <div className="form-section">
                <h2 className="section-title">Current Images</h2>
                
                <div className="image-preview-grid">
                  {images.map((image, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={image.url} alt={`Product ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeExistingImage(index)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Images */}
            <div className="form-section">
              <h2 className="section-title">Add New Images</h2>
              
              <div className="image-upload-container">
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/*"
                  multiple
                  onChange={handleNewImageChange}
                  style={{ display: 'none' }}
                  disabled={saving}
                />
                <label htmlFor="imageUpload" className="btn btn-secondary upload-btn">
                  📁 Choose Images
                </label>
              </div>
              
              {newImagePreviews.length > 0 && (
                <div className="image-preview-grid">
                  {newImagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={preview} alt={`New ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeNewImage(index)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="form-section">
              <h2 className="section-title">Specifications</h2>
              
              {specs.map((spec, index) => (
                <div key={index} className="spec-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Key"
                    value={spec.key || ''}
                    onChange={(e) => updateSpec(index, 'key', e.target.value)}
                    disabled={saving}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Value"
                    value={spec.value || ''}
                    onChange={(e) => updateSpec(index, 'value', e.target.value)}
                    disabled={saving}
                  />
                  <input
                    type="text"
                    className="form-input spec-unit"
                    placeholder="Unit"
                    value={spec.unit || ''}
                    onChange={(e) => updateSpec(index, 'unit', e.target.value)}
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => removeSpec(index)}
                    disabled={saving}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                className="btn btn-link"
                onClick={addSpec}
                disabled={saving}
              >
                + Add Specification
              </button>
            </div>

            {/* Keywords */}
            <div className="form-section">
              <h2 className="section-title">Keywords</h2>
              
              <div className="keyword-input-row">
                <input
                  type="text"
                  className="form-input"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add keyword"
                  disabled={saving}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addKeyword}
                  disabled={saving}
                >
                  Add
                </button>
              </div>
              
              {keywords.length > 0 && (
                <div className="keyword-tags">
                  {keywords.map((keyword, index) => (
                    <span key={index} className="keyword-tag">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(index)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                disabled={saving} 
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <span className="spinner"></span>
                    Updating...
                  </>
                ) : (
                  'Update Product'
                )}
              </button>
              <button 
                type="button" 
                onClick={() => navigate('/products')} 
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
